import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import type { ProfileSendService } from 'fym-shared/src/music24/profileSend';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { sendAllMessages as s } from './sendAllMessages';
import { failedCount, retryTargets, type SendAllItemView } from './sendAllModel';
import { useSendAll } from './useSendAll';
import { useSendAllConnect } from './useSendAllConnect';
import { useSendAllConnectReturn } from './useSendAllConnectReturn';
import { SendAllServiceRow } from './SendAllServiceRow';
import { SendAllOptions } from './SendAllOptions';
import { SendAllHandList } from './SendAllHandList';
import { SendAllFooter } from './SendAllFooter';
import { SendAllLead } from './SendAllLead';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Send to all sheet; each part uses its own slice of it */
import styles from './SendAll.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SendAllSheetProps {
  accountId: number;
  profile: ArtistProfileResponse;
  /** Back closes the sheet: it is a step of the history (`&sheet=sendAll`). */
  onClose: () => void;
  /**
   * The Spotify upload of the Profile (`&sheet=spotify`) opens this sheet with only Spotify
   * ticked; null ticks every service that can take the profile.
   */
  only?: ProfileSendService | null;
}

/**
 * "Send to all" (design profile-card c1–c6): the services that Music24 sends to, with a tick per
 * item, the rights line, "Keep my profiles in sync", and the profiles to set by hand. The live
 * state of each item comes from `profile.send.updated` (`ArtistProfile` tag); with no socket the
 * read polls while a send works. Below 576 px the sheet fills the screen (c6).
 */
const SendAllSheetComponent: React.FC<SendAllSheetProps> = ({ accountId, profile, onClose, only = null }) => {
  const intl = useIntl();
  const state = useSendAll(accountId, profile, only);
  const { overview, services, ticks, isWorking, toggle, send, isSending, startFailed } = state;
  const reload = useCallback(() => {
    overview.refetch().catch(console.error);
  }, [overview]);
  const { connect, failed: connectStartFailed } = useSendAllConnect({ accountId, onConnected: reload });
  const connectReturnFailed = useSendAllConnectReturn(reload);
  const onRetry = useCallback((view: SendAllItemView) => send(retryTargets(view)), [send]);
  const noneConnected = state.lead === 'noneConnected';

  return (
    <Modal isOpen toggle={onClose} centered scrollable size="lg" fullscreen="sm" data-testid="send-all-sheet">
      <ModalHeader toggle={onClose}>
        {intl.formatMessage(s.title)}
        <small className={`${styles.itemSub} d-block fw-normal`}>{intl.formatMessage(s.subtitle)}</small>
      </ModalHeader>
      <ModalBody className="d-flex flex-column gap-3">
        {overview.isLoading && <LoadingView />}
        {!overview.isLoading && overview.data === undefined && <ErrorView error={overview.error} retry={reload} />}
        {overview.data !== undefined && (
          <>
            <SendAllLead lead={state.lead} failed={failedCount(services)} footer={state.footer} />
            {(connectStartFailed || connectReturnFailed) && (
              <div className={styles.itemError} role="alert" data-testid="send-all-connect-failed">
                {intl.formatMessage(s.connectFailed)}
              </div>
            )}
            <div className={clsx('d-flex flex-column gap-3', noneConnected && styles.showFolded)}>
              {services.map((view) => (
                <SendAllServiceRow
                  key={view.service}
                  view={view}
                  profile={profile}
                  ticks={ticks}
                  only={only}
                  locked={isWorking}
                  isSending={isSending}
                  onToggle={toggle}
                  onRetry={onRetry}
                  onConnect={connect}
                />
              ))}
            </div>
            <SendAllOptions state={state} />
            {startFailed && (
              <div className={styles.itemError} role="alert" data-testid="send-all-start-failed">
                {intl.formatMessage(s.startFailed)}
              </div>
            )}
            <SendAllHandList accountId={accountId} profile={profile} />
          </>
        )}
      </ModalBody>
      {overview.data !== undefined && (
        <ModalFooter className="p-0">
          <SendAllFooter state={state} onClose={onClose} />
        </ModalFooter>
      )}
    </Modal>
  );
};

export const SendAllSheet = TypedMemo(SendAllSheetComponent);
